import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  listArticleDrafts,
  runArticleAutowriteNow,
  setArticleDraftStatus,
  updateArticleDraft,
  getAutoPublishSetting,
  setAutoPublishSetting,
  type ArticleDraft,
} from "@/lib/article-autowrite.functions";

const STATUS_LABEL: Record<ArticleDraft["status"], string> = {
  draft: "Osnutek",
  published: "Objavljen",
  rejected: "Zavrnjen",
};

export function AutoArticlesSection() {
  const load = useServerFn(listArticleDrafts);
  const runNow = useServerFn(runArticleAutowriteNow);
  const setStatus = useServerFn(setArticleDraftStatus);
  const saveDraft = useServerFn(updateArticleDraft);
  const readSetting = useServerFn(getAutoPublishSetting);
  const writeSetting = useServerFn(setAutoPublishSetting);

  const [rows, setRows] = useState<ArticleDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [autoPublish, setAutoPublish] = useState(false);
  const [editing, setEditing] = useState<ArticleDraft | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [list, setting] = await Promise.all([load(), readSetting()]);
      setRows(list);
      setAutoPublish(setting.enabled);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Napaka pri branju člankov.");
    } finally {
      setLoading(false);
    }
  }, [load, readSetting]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleRun = async () => {
    setRunning(true);
    try {
      const result = await runNow();
      if (result.ok) toast.success(`Pripravljen članek: ${result.title} (${result.words} besed)`);
      else toast.error(result.error);
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Napaka pri pripravi članka.");
    } finally {
      setRunning(false);
    }
  };

  const changeStatus = async (id: string, status: ArticleDraft["status"]) => {
    try {
      await setStatus({ data: { id, status } });
      await refresh();
      toast.success("Stanje posodobljeno.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Napaka.");
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      await saveDraft({
        data: {
          id: editing.id,
          title: editing.title,
          excerpt: editing.excerpt,
          content: editing.content,
        },
      });
      setEditing(null);
      await refresh();
      toast.success("Članek shranjen.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Napaka pri shranjevanju.");
    }
  };

  const toggleAutoPublish = async (enabled: boolean) => {
    setAutoPublish(enabled);
    try {
      await writeSetting({ data: { enabled } });
      toast.success(enabled ? "Samodejna objava vklopljena." : "Samodejna objava izklopljena.");
    } catch (e) {
      setAutoPublish(!enabled);
      toast.error(e instanceof Error ? e.message : "Napaka.");
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>Samodejni članki</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={autoPublish}
                onChange={(e) => void toggleAutoPublish(e.target.checked)}
              />
              Objavi samodejno, brez mojega pregleda
            </label>
            <Button onClick={() => void handleRun()} disabled={running}>
              {running ? "Pišem…" : "Pripravi članek zdaj"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Nov članek nastane samodejno vsak ponedeljek in sredo zjutraj. Temo izbere po podatkih
          Google Search Console; če ni priložnosti nad pragom, vzame naslednjo temo z uredniškega
          seznama. O vsakem novem članku dobite e-sporočilo.
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground">Nalagam…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Še ni pripravljenih člankov.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <Card key={r.id}>
              <CardContent className="space-y-2 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{r.title}</p>
                    <p className="text-sm text-muted-foreground">{r.excerpt}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      /clanek/{r.slug} · {r.word_count} besed · {STATUS_LABEL[r.status]}
                      {r.keyword ? ` · ključna beseda: ${r.keyword}` : ""}
                    </p>
                    {r.reason ? <p className="mt-1 text-xs text-muted-foreground">{r.reason}</p> : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(r)}>
                      Uredi
                    </Button>
                    {r.status !== "published" && (
                      <Button size="sm" onClick={() => void changeStatus(r.id, "published")}>
                        Objavi
                      </Button>
                    )}
                    {r.status === "published" && (
                      <Button variant="outline" size="sm" onClick={() => void changeStatus(r.id, "draft")}>
                        Umakni
                      </Button>
                    )}
                    {r.status !== "rejected" && (
                      <Button variant="outline" size="sm" onClick={() => void changeStatus(r.id, "rejected")}>
                        Zavrni
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing ? (
        <Card>
          <CardHeader>
            <CardTitle>Urejanje: {editing.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            />
            <textarea
              className="w-full rounded-md border px-3 py-2 text-sm"
              rows={2}
              value={editing.excerpt}
              onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
            />
            <textarea
              className="w-full rounded-md border px-3 py-2 font-mono text-xs"
              rows={18}
              value={editing.content}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
            />
            <div className="flex gap-2">
              <Button onClick={() => void handleSave()}>Shrani</Button>
              <Button variant="outline" onClick={() => setEditing(null)}>
                Prekliči
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
